import type { SelectOption, SelectSize } from '@property.com.au/ui-kit';
import {
  Button,
  Inline,
  mediaHelper,
  paletteHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent, HTMLProps } from 'react';
import styled, { css } from 'styled-components';

const getRadioButtonWidth = (radioValue: string): string => {
  switch (radioValue) {
    case '0':
      return '60px';
    case 'studio':
      return '80px';
    default:
      return '50px';
  }
};

interface LabelProps extends HTMLProps<HTMLLabelElement> {
  width?: string;
}

const Label = styled.label<LabelProps>`
  position: relative;
  margin-top: ${spacingHelper('twoExtraSmall')};

  flex: 0 1 ${(props) => props.width};

  ${mediaHelper('mobile')} {
    .buttonLabel {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &:focus-within,
  &:hover {
    div {
      background-color: ${paletteHelper('backgroundPrimaryInverted')};
      border-color: ${paletteHelper('borderSelected')};
      color: ${paletteHelper('textInverted')};
    }
  }
`;

const RadioInput = styled.input`
  opacity: 0;
  position: absolute;
  height: 100%;
  width: 100%;
`;

const ButtonWithActiveStyle = styled(Button)<{ $isActive: boolean }>`
  color: ${paletteHelper('textSecondary')};
  padding-left: ${spacingHelper('small')};
  padding-right: ${spacingHelper('small')};
  border-color: ${paletteHelper('borderPrimary')};
  font-weight: 500;

  &:focus {
    background-color: ${paletteHelper('backgroundPrimaryInverted')};
    border-color: ${paletteHelper('borderSelected')};
    color: ${paletteHelper('textInverted')};
  }

  ${({ $isActive }) => {
    return (
      $isActive &&
      css`
        background-color: ${paletteHelper('backgroundPrimaryInverted')};
        border-color: ${paletteHelper('borderSecondary')};
        color: ${paletteHelper('textInverted')};
      `
    );
  }}
`;
interface RadioButtonProps {
  isSelected: boolean;
  option: SelectOption;
  sizeVariant?: SelectSize;
  name: string;
  onChange: (selection: SelectOption) => void;
  width: string;
}
export const RadioButton: FunctionComponent<RadioButtonProps> = ({
  isSelected,
  option,
  sizeVariant = 'medium',
  name,
  onChange,
  width,
}) => {
  return (
    <Label width={width} htmlFor={`${name}_${option.value}`}>
      <RadioInput
        checked={isSelected}
        id={`${name}_${option.value}`}
        name={`${name}_${option.value}`}
        type="radio"
        value={option.value}
        onChange={() => onChange(option)}
      />
      <ButtonWithActiveStyle
        forwardedAs="div"
        $isActive={isSelected}
        variant="outline"
        sizeVariant={sizeVariant}
        className="buttonLabel"
      >
        {option.label}
      </ButtonWithActiveStyle>
    </Label>
  );
};

const RadioGroupItems = styled(Inline)`
  gap: ${spacingHelper('extraSmall')};
  flex-wrap: wrap;
`;

const RadioGroupLabel = styled.span`
  ${typographyHelper('subtitle01')}
  font-weight: 500;
  font-size: 1rem;
`;

const RadioGroup = styled.div`
  width: 100%;
`;

interface RadioControlButtonsProps {
  label: string;
  options: SelectOption[];
  onChange: (opt: SelectOption) => void;
  selected: SelectOption | null;
  sizeVariant?: SelectSize;
  name: string;
}
export const SearchRadioButtonGroup: FunctionComponent<RadioControlButtonsProps> = ({
  label,
  selected,
  options,
  name,
  ...props
}) => {
  return (
    <RadioGroup>
      <RadioGroupLabel>{label}</RadioGroupLabel>
      <RadioGroupItems gap="0" grow={false} style={{ width: '97%' }}>
        {options.map((option) => {
          return (
            <RadioButton
              key={option.value}
              isSelected={option.value === selected?.value}
              option={option}
              name={name}
              width={getRadioButtonWidth(option.value)}
              {...props}
            />
          );
        })}
      </RadioGroupItems>
    </RadioGroup>
  );
};
